// ABOUTME: Applying in up to three steps (PJL, B1–B3): the week, the PRO CV when there is none, the last check. Each step
// ABOUTME: records its funnel step; sending carries the week, and a talent without a CV goes on into the CV builder.
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import { AppButton } from 'components/AppButton';
import { BottomSheet } from 'components/BottomSheet';
import { Text } from 'components/Text';
import { PATHS } from 'config/paths';
import { sendAtsJobApplied, sendAtsJobApplyFailed } from 'modules/analytics';
import type { TimeRange, Weekday } from 'modules/availability/types';
import { getTrans, getTransWithParams, TranslationKey } from 'modules/i18n';
import { Iconify } from 'modules/minimal-ui-theme';
import { ApiError } from 'utils/api';
import {
  APPLY_NOT_POSSIBLE_ERROR_CODE,
  JOB_LISTING_NOT_FOUND_ERROR_CODE,
  JOB_LISTINGS_UNAVAILABLE_ERROR_CODE
} from '../../constants';
import {
  useApplyToJobListing,
  useListingEngagement,
  useWeekFit
} from '../../hooks';
import type { JobListing } from '../../types';
import { CvStep, ReviewStep } from './ReviewStep';
import {
  changedWeekRequest,
  editedDays,
  hasEdits,
  WeekEdits
} from './weekDraft';
import { WeekStep } from './WeekStep';

type Step = 'week' | 'cv' | 'review';

const TITLE: Record<Step, TranslationKey> = {
  week: 'jobs.apply.week.title',
  cv: 'jobs.apply.cv.title',
  review: 'jobs.apply.review.title'
};

const ERROR_COPY: Record<string, TranslationKey> = {
  [APPLY_NOT_POSSIBLE_ERROR_CODE]: 'jobs.apply.error.notPossible',
  [JOB_LISTING_NOT_FOUND_ERROR_CODE]: 'jobs.apply.error.gone'
};

/**
 * Apply v2 refuses a week it cannot take with 400: the shape (`error.parameters.not-valid`), or, with saveToProfile, the
 * availability PUT's own rules (`error.validator.update-availability.*`). Nothing was sent or saved then.
 */
const WEEK_REFUSED_PREFIX = 'error.validator.update-availability.';
const PARAMETERS_NOT_VALID_ERROR_CODE = 'error.parameters.not-valid';

const errorCopy = (code: string): TranslationKey => {
  if (ERROR_COPY[code]) return ERROR_COPY[code];
  if (
    code === PARAMETERS_NOT_VALID_ERROR_CODE ||
    code.startsWith(WEEK_REFUSED_PREFIX)
  )
    return 'jobs.apply.error.week';
  return 'jobs.apply.error.unnamed';
};

const codeOf = (error: unknown): string =>
  error instanceof ApiError ? error.code : '';

export const ApplyFlow = ({
  listing,
  hasCv,
  open,
  onClose,
  onUnavailable
}: {
  listing: JobListing;
  hasCv: boolean;
  open: boolean;
  onClose: () => void;
  /**
   * pro-core answered the week check with the 404 of PRO_APPLY_V2 being off for this talent: the caller shows today's
   * apply instead, so the talent is never stuck in a flow the server does not serve.
   */
  onUnavailable: () => void;
}) => {
  const navigate = useNavigate();
  const record = useListingEngagement(listing.refId, 'apply');
  const { apply, isApplying, errorCode, reset } = useApplyToJobListing(
    listing.refId
  );
  const steps: Step[] = hasCv ? ['week', 'review'] : ['week', 'cv', 'review'];
  const [step, setStep] = useState<Step>('week');
  const [edits, setEdits] = useState<WeekEdits>({});
  const [editing, setEditing] = useState<Weekday | null>(null);
  const [weeklyMinutes, setWeeklyMinutes] = useState<number | undefined>();
  const [startFrom, setStartFrom] = useState<string | undefined>();
  const [holidaysOk, setHolidaysOk] = useState(false);
  const [saveToProfile, setSaveToProfile] = useState(true);
  const [locationConfirmed, setLocationConfirmed] = useState(false);

  // The profile's week is asked once; an edited week, hours or start is asked again on every change, the profile's
  // answer kept.
  const profile = useWeekFit(listing.refId, {}, open);
  const days = useMemo(
    () => editedDays(profile.answer, edits),
    [profile.answer, edits]
  );
  const start =
    startFrom ?? profile.answer?.profile.startFrom ?? listing.startDate;
  const changed = changedWeekRequest(
    days,
    edits,
    weeklyMinutes,
    startFrom,
    start
  );
  const edited = useWeekFit(listing.refId, changed ?? {}, open && !!changed);
  const answer = changed ? edited.answer ?? profile.answer : profile.answer;
  const minutes =
    weeklyMinutes ??
    profile.answer?.profile.weeklyMinutes ??
    listing.maxWeeklyHourMinutes;
  const needsLocation = (listing.autoRequirements ?? []).includes(
    'LOCATION_SHARING'
  );
  const asksHolidays =
    answer?.weekend?.holidays === 'OCCASIONAL' ||
    answer?.weekend?.holidays === 'REGULAR';

  useEffect(() => {
    if (open) record('APPLY_STARTED');
  }, [open, record]);

  const serverOff = profile.errorCode === JOB_LISTINGS_UNAVAILABLE_ERROR_CODE;
  useEffect(() => {
    if (serverOff) onUnavailable();
  }, [serverOff, onUnavailable]);

  const index = steps.indexOf(step);
  const goTo = (next: Step) => {
    if (step === 'week') record('APPLY_WEEK_FIT_DONE');
    if (step === 'cv') record('APPLY_CV_DONE');
    if (next === 'review') record('APPLY_REVIEW_OPENED');
    setStep(next);
  };

  const close = () => {
    reset();
    onClose();
  };

  const send = async () => {
    try {
      const answerOfApply = await apply({
        availability: {
          days,
          weeklyMinutes: minutes,
          startFrom: start || undefined,
          workOnHolidays: asksHolidays ? holidaysOk : undefined
        },
        saveToProfile: hasEdits(edits) && saveToProfile,
        confirmations: needsLocation ? ['LOCATION_SHARING'] : undefined
      });
      sendAtsJobApplied({
        jobListingRefId: listing.refId,
        applicationRefId: answerOfApply.applicationRefId,
        created: answerOfApply.created
      });
      onClose();
      if (answerOfApply.created && answerOfApply.nextAction === 'CV') {
        navigate(PATHS.cvBuilder);
      } else if (answerOfApply.created) {
        navigate(PATHS.applications, {
          state: { applicationSent: answerOfApply.applicationRefId }
        });
      } else {
        navigate(`${PATHS.applications}/${answerOfApply.applicationRefId}`);
      }
    } catch (error) {
      sendAtsJobApplyFailed({
        jobListingRefId: listing.refId,
        errorCode: codeOf(error) || 'error.unnamed'
      });
    }
  };

  const title = getTrans(TITLE[step]);

  return (
    <BottomSheet
      open={open}
      onClose={close}
      ariaLabel={title}
      disableBackdropClose
    >
      <Box
        data-testid="apply-flow"
        data-step={step}
        sx={{
          p: 3,
          display: 'grid',
          gap: 2,
          overflowY: 'auto',
          maxHeight: '88vh'
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
          <Box>
            <Text
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 700 }}
            >
              {getTransWithParams('jobs.apply.flow.step', {
                current: index + 1,
                total: steps.length
              })}
            </Text>
            <Text
              variant="h5"
              component="h2"
              color="text.primary"
              sx={{ fontWeight: 700 }}
            >
              {title}
            </Text>
          </Box>
          <IconButton
            aria-label={getTrans('jobs.apply.flow.close')}
            onClick={close}
            data-testid="apply-flow-close"
          >
            <Iconify icon="eva:close-fill" />
          </IconButton>
        </Box>

        {step === 'week' && (
          <WeekStep
            listing={listing}
            answer={answer}
            profileAnswer={profile.answer}
            isError={profile.isError || edited.isError}
            isChecking={!!changed && edited.isChecking}
            edits={edits}
            editing={editing}
            onEdit={setEditing}
            onChangeDay={(day: Weekday, ranges: TimeRange[]) =>
              setEdits(current => ({ ...current, [day]: ranges }))
            }
            weeklyMinutes={minutes}
            onWeeklyMinutes={setWeeklyMinutes}
            startFrom={start}
            onStartFrom={setStartFrom}
            holidaysOk={holidaysOk}
            onHolidaysOk={setHolidaysOk}
            saveToProfile={saveToProfile}
            onSaveToProfile={setSaveToProfile}
          />
        )}
        {step === 'cv' && <CvStep />}
        {step === 'review' && (
          <ReviewStep
            listing={listing}
            days={days}
            weeklyMinutes={minutes}
            startFrom={start}
            needsLocation={needsLocation}
            locationConfirmed={locationConfirmed}
            onLocationConfirmed={setLocationConfirmed}
          />
        )}

        {errorCode && (
          <Alert severity="error" data-testid="apply-flow-error">
            {getTrans(errorCopy(errorCode))}
          </Alert>
        )}

        {!editing && (
          <Box
            sx={{ display: 'flex', gap: 1.5, justifyContent: 'space-between' }}
          >
            <AppButton
              variant="text"
              testId="apply-flow-back"
              disabled={index === 0}
              onClick={() => setStep(steps[index - 1])}
            >
              {getTrans('jobs.apply.flow.back')}
            </AppButton>
            {step === 'review' ? (
              <AppButton
                testId="apply-flow-send"
                disabled={
                  isApplying ||
                  edited.isChecking ||
                  edited.isError ||
                  (needsLocation && !locationConfirmed)
                }
                onClick={() => void send()}
              >
                {getTrans('jobs.apply.review.send')}
              </AppButton>
            ) : (
              <AppButton
                testId="apply-flow-next"
                // Without the profile's answer `days` holds only the edits; while an edited week is read, the next step
                // would show the old fit.
                disabled={
                  !profile.answer || edited.isChecking || edited.isError
                }
                onClick={() => goTo(steps[index + 1])}
              >
                {getTrans('jobs.apply.flow.next')}
              </AppButton>
            )}
          </Box>
        )}
      </Box>
    </BottomSheet>
  );
};
